import React from 'react';
import type { Card, CardMetric } from '@ctd/agent-contract';
import { formatCalendarDate, formatMetricValue } from '../utils/card-format';
import { seriesOf } from '../utils/card-series';
import type { SeriesDay } from '../utils/card-series';
import { CardFrame } from './card-frame';
import { MetricBlock, MetricFigures } from './metric-block';
import style from '../style.module.scss';

const WIDTH = 200;
const HEIGHT = 54;
const PAD_X = 2;
const PAD_Y = 4;

const timeOf = (date: string): number => Date.parse(`${date}T00:00:00Z`);

const coord = (value: number): string => value.toFixed(2);

function DayLine({ metric, days }: { metric: CardMetric; days: SeriesDay[] }) {
    const known = days.filter(({ value }) => value !== null);
    if (known.length === 0) {
        return null;
    }

    const values = known.map(({ value }) => value ?? 0);
    const lowest = Math.min(...values);
    const range = Math.max(...values) - lowest;
    const first = timeOf(days[0].date);
    const span = timeOf(days[days.length - 1].date) - first;

    const xOf = (date: string): number =>
        span === 0
            ? WIDTH / 2
            : PAD_X + ((timeOf(date) - first) / span) * (WIDTH - 2 * PAD_X);
    const yOf = (value: number): number =>
        range === 0
            ? HEIGHT / 2
            : PAD_Y + (1 - (value - lowest) / range) * (HEIGHT - 2 * PAD_Y);

    const placed = days.map(({ date, value }) => ({
        date,
        value,
        x: xOf(date),
        y: value === null ? null : yOf(value),
    }));

    const runs: string[][] = [];
    let run: string[] | undefined;
    for (const { x, y } of placed) {
        if (y === null) {
            run = undefined;
            continue;
        }
        if (!run) {
            run = [];
            runs.push(run);
        }
        run.push(`${coord(x)} ${coord(y)}`);
    }
    const path = runs
        .map(
            (points) =>
                `M${(points.length > 1 ? points : [...points, ...points]).join(
                    'L'
                )}`
        )
        .join('');

    const xs = placed.map(({ x }) => x);

    return (
        <svg
            className={style.day_line}
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={`${metric.label} per day`}
        >
            <path className={style.day_line_path} d={path} />
            {placed.map(({ date, value, x }, index) => {
                if (value === null) {
                    return null;
                }
                const before = index > 0 ? xs.at(index - 1) : undefined;
                const after = xs.at(index + 1);
                const left =
                    before !== undefined
                        ? (before + x) / 2
                        : after !== undefined
                        ? x - (after - x) / 2
                        : 0;
                const right =
                    after !== undefined
                        ? (x + after) / 2
                        : before !== undefined
                        ? x + (x - before) / 2
                        : WIDTH;
                const from = Math.max(0, left);
                return (
                    <rect
                        key={date}
                        className={style.day_line_hover}
                        x={coord(from)}
                        y={0}
                        width={coord(Math.min(WIDTH, right) - from)}
                        height={HEIGHT}
                    >
                        <title>
                            {`${formatCalendarDate(date)}: ${formatMetricValue({
                                unit: metric.unit,
                                value,
                            })}`}
                        </title>
                    </rect>
                );
            })}
        </svg>
    );
}

export const SeriesCard: React.FC<{
    card: Extract<Card, { shape: 'series' }>;
}> = ({ card }) => (
    <CardFrame card={card}>
        <div className={style.metrics}>
            {card.primary.map((metric) => (
                <MetricBlock key={metric.id} metric={metric} title={card.title}>
                    <MetricFigures metric={metric} />
                    <DayLine
                        metric={metric}
                        days={seriesOf(card.series, metric.id)}
                    />
                </MetricBlock>
            ))}
        </div>
    </CardFrame>
);
